// Faculty Orientation 2026-2027 — King's Academy (React)
// Self-contained. Default export, no required props.
// Landing: pick who you are. Schedule is one dataset, filtered by audience.
//
// Sources: "New Faculty Orientation (August 8-11, 2026)" and
//          "Faculty Orientation DRAFT August 2026 v4 (August 10-13, 2026)".
// Nothing here is invented; see the ASSUMPTIONS block at the bottom of this file
// for every place the two source documents disagreed or left a value blank.

import { useState, useEffect } from "react";

const DAY_START = 420;   // 7:00 am
const DAY_END = 1230;    // 8:30 pm
const PPM = 1.8;         // pixels per minute
const TOP_PAD = 18;
const MOBILE_BP = 768;

const overlaps = (a, b) => a.s < b.e && b.s < a.e;

const COLORS = {
  navy: "#002D62", red: "#EE3124", gold: "#A39161", beige: "#E8E6DF",
  navyTint: "#EAEFF6", redTint: "#FBE9E8", redInk: "#9b1a16",
  sharedFill: "#E8E6DF", famEdge: "#7e6f44", famInk: "#5f5230",
  grid: "#e8e3d8",
};

// Category drives colour. Every block declares one.
const CAT_STYLE = {
  newfac:    { bg: COLORS.navyTint,   edge: COLORS.navy,  ink: COLORS.navy,     tag: "New faculty" },
  all:       { bg: COLORS.sharedFill, edge: COLORS.gold,  ink: COLORS.navy,     tag: "All faculty" },
  logistics: { bg: COLORS.redTint,    edge: COLORS.red,   ink: COLORS.redInk,   tag: "Logistics" },
  social:    { bg: "#333B49",         edge: COLORS.gold,  ink: "#FFFFFF",       tag: "Meal or social" },
};
const catStyle = (c) => CAT_STYLE[c] || CAT_STYLE.all;

const FONT_BODY = '"Helvetica Neue", Helvetica, Arial, "Segoe UI", system-ui, sans-serif';
const FONT_DISPLAY = '"Clarice", Georgia, "Times New Roman", serif';

// ---- audiences ----
const NEW = "newFaculty", RET = "returningFaculty", LEAD = "leadership", MEN = "mentor";
const ALL4 = [NEW, RET, LEAD, MEN];
const NEW_PROGRAM = [NEW, LEAD, MEN];   // the Aug 9-10 new-faculty programme
const NEW_MEN = [NEW, MEN];
const NEW_LEAD = [NEW, LEAD];

const AUDIENCES = [
  { key: NEW, label: "New Faculty" },
  { key: RET, label: "Returning Faculty" },
  { key: LEAD, label: "Leadership, Department Heads and Class Deans" },
  { key: MEN, label: "Mentor" },
  { key: "all", label: "Full Master Schedule" },
];
const audLabel = (k) => (AUDIENCES.find((a) => a.key === k) || {}).label || "Everyone";

function hour12(m) {
  const h = Math.floor(m / 60);
  const ap = (h % 24) < 12 ? "AM" : "PM";
  let hh = h % 12;
  if (hh === 0) hh = 12;
  return hh + " " + ap;
}

function packEvents(events) {
  const ev = events.map((e, i) => ({ ...e, _i: i })).sort((a, b) => a.s - b.s || a.e - b.e);
  const colEnd = [];
  ev.forEach((x) => {
    let placed = false;
    for (let i = 0; i < colEnd.length; i++) {
      if (colEnd[i] <= x.s) { x.col = i; colEnd[i] = x.e; placed = true; break; }
    }
    if (!placed) { x.col = colEnd.length; colEnd.push(x.e); }
  });
  ev.forEach((x) => (x.cl = -1));
  let c = 0;
  for (const a of ev) {
    if (a.cl < 0) {
      a.cl = c;
      let changed = true;
      while (changed) {
        changed = false;
        for (const b of ev) if (b.cl >= 0)
          for (const d of ev) if (d.cl < 0 && overlaps(b, d)) { d.cl = b.cl; changed = true; }
      }
      c++;
    }
  }
  const counts = {};
  ev.forEach((x) => (counts[x.cl] = Math.max(counts[x.cl] || 0, x.col + 1)));
  ev.forEach((x) => (x.of = counts[x.cl]));
  return ev;
}

/* ---------------- data: one source of truth ---------------- */
/*  s / e   start and end in minutes past midnight
    cat     newfac | all | logistics | social
    t       display time string
    l       label
    w       presenter or descriptive line
    n       location and notes
    org     organiser credit
    rota    optional sub-schedule rendered inside the block
    tag     overrides the category chip
    for     which audiences see the item
    family  true when spouses or families are included                        */

const DAYS = [
  {
    meta: "Saturday · August 8, 2026",
    title: "New Faculty Arrival and Welcome Dinner",
    pop: "New faculty, their spouses and school leadership",
    head: "New Faculty and Leadership",
    axisStart: 1050,
    axisEnd: 1200,
    ppm: 5.0,
    emptyNote: "Returning faculty join on Monday, August 10 for the welcome back dinner.",
    events: [
      {
        s: 1110, e: 1140, cat: "social", t: "6:30 – 7:00 pm",
        l: "Official Welcome Dinner",
        w: "Hosted by Head of School Mrs. Penny Townsend",
        n: "Beit Al Mudeera, outside · Department heads introduce each member of their department",
        org: "Selina Halteh", family: true,
        tag: "New faculty, leadership, class deans and HoDs",
        for: NEW_LEAD,
      },
    ],
    milestones: [],
  },

  {
    meta: "Sunday · August 9, 2026",
    title: "New Faculty Orientation — Day 1",
    pop: "New faculty, mentors, department heads and leadership",
    head: "New Faculty Orientation",
    dress: "Academic dress code for the ID photo shoot",
    notes: ["Bring your passport."],
    axisStart: 480,
    axisEnd: 1200,
    emptyNote: "Returning faculty join on Monday, August 10 for the welcome back dinner.",
    events: [
      {
        s: 510, e: 570, cat: "newfac", t: "8:30 – 9:30 am",
        l: "Morning Coffee and Breakfast",
        w: "Welcome and introduction by Head of School Mrs. Penny Townsend",
        n: "Arab Bank Administration Building (ABAB) G01",
        org: "James Meyer", meal: true,
        tag: "New faculty, HoS, HoDs, leadership and mentors",
        for: NEW_PROGRAM,
      },
      {
        s: 600, e: 660, cat: "newfac", t: "10:00 – 11:00 am",
        l: "Living and Working at King's",
        w: "The challenges, complexities and rewards ahead. Building your tenure at KA.",
        n: "Arab Bank Administration Building (ABAB) G01",
        org: "James Meyer",
        tag: "New faculty, HoDs, leadership and class deans",
        for: NEW_PROGRAM,
      },
      {
        s: 660, e: 780, cat: "logistics", t: "11:00 am – 1:00 pm",
        l: "Logistics",
        n: "Arab Bank Administration Building (ABAB) G01",
        org: "James Meyer and Razan Jbara",
        tag: "New faculty, HoDs, mentors and class deans",
        rota: [
          { t: "Finance Office", l: "Bank accounts, salary and reimbursement" },
          { t: "General Administration", l: "Car rentals and phone bundles" },
          { t: "Human Resources", l: "Medical insurance, sign contracts" },
          { t: "Communications", l: "ID photo shoot" },
          { t: "IT", l: "Pick up laptops" },
        ],
        for: NEW_PROGRAM,
      },
      {
        s: 780, e: 825, cat: "social", t: "1:00 – 1:45 pm",
        l: "Walk-through Lunch",
        n: "The Hess Family Dining Hall",
        meal: true,
        tag: "New faculty, HoDs and mentors",
        for: NEW_PROGRAM,
      },
      {
        s: 825, e: 960, cat: "newfac", t: "1:45 – 4:00 pm",
        l: "Meet with Your Department Head",
        w: "Review class assignments, students and schedules",
        tag: "New faculty and department heads",
        for: NEW_LEAD,
      },
      {
        s: 1020, e: 1140, cat: "social", t: "5:00 pm",
        l: "Dinner at Beit Sitti",
        w: "Hosted by Head of School Mrs. Penny Townsend",
        n: "Off campus · end time to be confirmed",
        org: "Selina Halteh", meal: true,
        tag: "New faculty and mentors",
        for: NEW_MEN,
      },
    ],
    milestones: [
      { at: 960, kind: "transport", time: "4:00 pm", label: "Depart from the Middle School for Beit Sitti", for: NEW_MEN },
    ],
  },

  {
    meta: "Monday · August 10, 2026",
    title: "New Faculty Orientation — Day 2 · All faculty return",
    pop: "New faculty by day, all faculty at dinner",
    head: "New Faculty Orientation and Welcome Back Dinner",
    axisStart: 420,
    axisEnd: 1200,
    emptyNote: "Your orientation begins with the welcome back dinner this evening.",
    events: [
      {
        s: 450, e: 510, cat: "social", t: "7:30 – 8:30 am",
        l: "Morning Coffee and Breakfast",
        n: "The Hess Family Dining Hall",
        org: "Razan Jbara", meal: true,
        tag: "New faculty, HoS, HoDs, leadership and mentors",
        for: NEW_PROGRAM,
      },
      {
        s: 510, e: 660, cat: "newfac", t: "8:30 – 11:00 am",
        l: "Introduction to King's Digital Platforms",
        n: "Arab Bank Administration Building (ABAB) G01",
        org: "Michael Kussaim Hussam, Khaled Albakri, Michael Barraclough and Hamzeh Qudah",
        tag: "New faculty, HoDs and mentors",
        rota: [
          { t: "IT Introduction", l: "" },
          { t: "Veracross", l: "" },
          { t: "Canvas", l: "" },
          { t: "Schooldude", l: "" },
          { t: "DAR", l: "" },
        ],
        for: NEW_PROGRAM,
      },
      {
        s: 660, e: 675, cat: "social", t: "11:00 – 11:15 am",
        l: "Coffee Break",
        n: "Arab Bank Administration Building (ABAB) G01",
        meal: true,
        for: NEW_PROGRAM,
      },
      {
        s: 720, e: 780, cat: "social", t: "12:00 – 1:00 pm",
        l: "Walk-through Lunch",
        n: "The Hess Family Dining Hall",
        meal: true,
        tag: "New faculty, HoDs and mentors",
        for: NEW_PROGRAM,
      },
      {
        s: 780, e: 960, cat: "newfac", t: "1:00 – 4:00 pm",
        l: "Work Time",
        w: "Course planning and room set-up",
        tag: "New faculty",
        for: [NEW],
      },
      {
        s: 1110, e: 1170, cat: "social", t: "6:30 – 7:30 pm",
        l: "Welcome Back Dinner",
        w: "With Head of School Mrs. Penny Townsend",
        n: "Tawfiq Fakhouri Courtyard",
        org: "Selina Halteh", family: true, meal: true,
        tag: "All faculty",
        for: ALL4,
      },
    ],
    milestones: [],
  },

  {
    meta: "Tuesday · August 11, 2026",
    title: "Faculty Orientation — Day 1 of 3",
    pop: "All faculty and leadership",
    head: "All Faculty and Leadership",
    dress: "Academic dress code required for the faculty photo",
    axisStart: 480,
    axisEnd: 1200,
    events: [
      {
        s: 510, e: 540, cat: "social", t: "8:30 – 9:00 am",
        l: "Morning Coffee and Continental Breakfast",
        w: "With Head of School Mrs. Penny Townsend",
        n: "G01",
        org: "Selina Halteh and James Meyer", meal: true,
        for: ALL4,
      },
      {
        s: 540, e: 585, cat: "all", t: "9:00 – 9:45 am",
        l: "Head of School Opening Remarks",
        w: "Introduction of new faculty",
        n: "G01",
        org: "Selina Halteh and James Meyer",
        for: ALL4,
      },
      {
        s: 585, e: 615, cat: "logistics", t: "9:45 – 10:15 am",
        l: "Faculty Photo",
        n: "Academic dress code required",
        for: ALL4,
      },
      {
        s: 615, e: 750, cat: "all", t: "10:15 am – 12:30 pm",
        l: "Leadership Updates",
        n: "G01",
        org: "James Meyer",
        rota: [
          { t: "Admissions", l: "Laura Burgess" },
          { t: "Dean of Academic Affairs", l: "Shaun Mayor" },
          { t: "Head of Upper School", l: "Mike Barraclough" },
          { t: "Head of Middle School", l: "Tala Issa" },
          { t: "CFO", l: "Micheal Farah" },
          { t: "Associate Head", l: "Kyle Sumrow" },
          { t: "Dean of Faculty", l: "James Meyer" },
        ],
        for: ALL4,
      },
      {
        s: 750, e: 795, cat: "social", t: "12:30 – 1:15 pm",
        l: "Sit-down Lunch",
        w: "Mealtime etiquette and modelling expectations",
        n: "The Hess Family Dining Hall · Returning faculty serve as waiters and servers. Ten tables with identified table heads.",
        meal: true,
        for: ALL4,
      },
      {
        s: 810, e: 960, cat: "all", t: "1:30 – 4:00 pm",
        l: "Safeguarding, Advisory and Student Behaviour",
        w: "Safeguarding presentation with role plays and case studies · Advisory roles and responsibilities · MOPS, dress code and student behaviour",
        n: "G01",
        org: "Michael Barraclough, James Meyer and Shaun Mayor",
        for: ALL4,
      },
      {
        s: 1110, e: 1170, cat: "social", t: "6:30 – 7:30 pm",
        l: "Faculty and Staff BBQ",
        n: "Refectory Square",
        meal: true,
        tag: "All faculty, staff and leadership",
        for: ALL4,
      },
    ],
    milestones: [],
  },

  {
    meta: "Wednesday · August 12, 2026",
    title: "Faculty Orientation — Day 2 of 3",
    pop: "Department meetings, health screening and advisory",
    head: "All Faculty and Leadership",
    axisStart: 480,
    axisEnd: 1200,
    events: [
      {
        s: 510, e: 540, cat: "social", t: "8:30 – 9:00 am",
        l: "Morning Coffee and Continental Breakfast",
        n: "Arab Bank Administration Building (ABAB) G01",
        meal: true,
        for: ALL4,
      },
      {
        s: 540, e: 780, cat: "all", t: "9:00 am – 1:00 pm",
        l: "Department Meetings",
        n: "Location TBA",
        for: ALL4,
      },
      {
        s: 540, e: 680, cat: "logistics", t: "9:00 – 11:20 am",
        l: "Blood Testing and Fingerprinting",
        w: "By department, during the department meeting block",
        n: "Arab Bank Administration Building (ABAB) G01",
        tag: "By department",
        rota: [
          { t: "9:00 – 9:20", l: "History, Religion and Society" },
          { t: "9:20 – 9:40", l: "Physical and Life Science, and Mathematics" },
          { t: "9:40 – 10:00", l: "CRLA Arabic and English" },
          { t: "10:00 – 10:20", l: "World Languages" },
          { t: "10:20 – 10:40", l: "Art, Design and Technology" },
          { t: "10:40 – 11:00", l: "Leadership and Administration" },
          { t: "11:00 – 11:20", l: "Athletics and PE" },
        ],
        for: ALL4,
      },
      {
        s: 780, e: 825, cat: "social", t: "1:00 – 1:45 pm",
        l: "Sit-down Lunch",
        n: "The Hess Family Dining Hall · Returning faculty serve as waiters and servers",
        meal: true,
        for: ALL4,
      },
      {
        s: 825, e: 885, cat: "all", t: "1:45 – 2:45 pm",
        l: "Book Group",
        w: "A More Beautiful Question",
        n: "Location TBA",
        for: ALL4,
      },
      {
        s: 900, e: 960, cat: "all", t: "3:00 – 4:00 pm",
        l: "Advisory Programme",
        w: "Grade level expectations, programme and preparations",
        n: "Location TBA",
        org: "Class deans and Michael Barraclough",
        for: ALL4,
      },
      {
        s: 1080, e: 1170, cat: "social", t: "6:00 – 7:30 pm",
        l: "Dinner",
        n: "The Hess Family Dining Hall",
        meal: true,
        for: ALL4,
      },
    ],
    milestones: [],
  },

  {
    meta: "Thursday · August 13, 2026",
    title: "Faculty Orientation — Day 3 of 3",
    pop: "Departmental meetings, grade level meetings and the AI workshop",
    head: "All Faculty and Leadership",
    axisStart: 420,
    axisEnd: 1230,
    events: [
      {
        s: 450, e: 510, cat: "social", t: "7:30 – 8:30 am",
        l: "Morning Coffee and Breakfast",
        n: "The Hess Family Dining Hall",
        meal: true,
        for: ALL4,
      },
      {
        s: 510, e: 720, cat: "all", t: "8:30 am – 12:00 pm",
        l: "Departmental Meetings",
        n: "Location TBD",
        rota: [
          { t: "History", l: "" },
          { t: "STEM", l: "" },
          { t: "VPA", l: "" },
          { t: "World Language", l: "" },
          { t: "English", l: "" },
          { t: "Athletics and PE", l: "" },
        ],
        for: ALL4,
      },
      {
        s: 720, e: 765, cat: "social", t: "12:00 – 12:45 pm",
        l: "Walk-through Lunch",
        n: "The Hess Family Dining Hall",
        meal: true,
        for: ALL4,
      },
      {
        s: 780, e: 840, cat: "all", t: "1:00 – 2:00 pm",
        l: "Grade Level Meeting",
        w: "Cell phones, dress code and attendance",
        n: "Location TBD",
        org: "Class deans",
        tag: "Grade level teachers",
        for: ALL4,
      },
      {
        s: 840, e: 960, cat: "all", t: "2:00 – 4:00 pm",
        l: "GOA AI Workshop",
        n: "G01 · Laptops are required",
        org: "James Meyer and Kyle Sumrow",
        for: ALL4,
      },
      {
        s: 1080, e: 1170, cat: "social", t: "6:00 – 7:30 pm",
        l: "Dinner",
        n: "The Hess Family Dining Hall",
        meal: true,
        for: ALL4,
      },
      {
        s: 1140, e: 1200, cat: "all", t: "7:00 – 8:00 pm",
        l: "Residential Life Meeting and House Meetings",
        n: "Location TBD",
        org: "Michael Barraclough",
        tag: "Residential life faculty and staff",
        for: ALL4,
      },
    ],
    milestones: [],
  },
];

/* ---------------- filtering ---------------- */

function visibleFor(x, aud) {
  if (aud === "all") return true;
  return Array.isArray(x.for) && x.for.includes(aud);
}
function filterDay(day, aud) {
  return {
    ...day,
    events: (day.events || []).filter((e) => visibleFor(e, aud)),
    milestones: (day.milestones || []).filter((m) => visibleFor(m, aud)),
  };
}
const dayIsEmpty = (d) => (d.events || []).length === 0 && (d.milestones || []).length === 0;

/* ---------------- shared bits ---------------- */

function Logo({ height = 46 }) {
  return <img src="/ka-logo.png" alt="King's Academy" style={{ height, width: "auto", display: "block", alignSelf: "flex-start", flex: "none" }} />;
}

function useIsMobile(bp = MOBILE_BP) {
  const [m, setM] = useState(typeof window !== "undefined" ? window.innerWidth < bp : false);
  useEffect(() => {
    const onResize = () => setM(window.innerWidth < bp);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [bp]);
  return m;
}

function LegendItem({ swatch, children }) {
  return <span style={{ display: "flex", alignItems: "center", gap: 8 }}>{swatch}{children}</span>;
}

function Swatch({ solid, dashed, dot }) {
  if (dot) return <span style={{ width: 14, height: 14, borderRadius: "50%", background: "#fff", border: `2px solid ${COLORS.gold}` }} />;
  if (dashed) return <span style={{ width: 26, height: 14, borderRadius: 3, background: "#fff", border: `1px dashed ${COLORS.gold}` }} />;
  return <span style={{ width: 26, height: 14, borderRadius: 3, background: solid }} />;
}

function Chip({ bg, children }) {
  return (
    <span style={{ display: "inline-block", marginLeft: 6, background: bg, color: "#fff", fontSize: 9, fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase", padding: "1px 5px", borderRadius: 3, verticalAlign: "middle" }}>
      {children}
    </span>
  );
}

/* ---------------- Desktop timeline ---------------- */

function EventBlock({ ev, delay, y, ht }) {
  const sub = 100 / ev.of;
  const gap = ev.of > 1 ? 1 : 0;
  const tight = ev.of >= 3;
  const s = catStyle(ev.cat);
  const dark = ev.cat === "social";
  const subInk = dark ? "rgba(255,255,255,.82)" : undefined;
  return (
    <div className="evt" style={{
      position: "absolute", top: y(ev.s), minHeight: ht(ev.s, ev.e) - 4,
      left: `${ev.col * sub}%`, width: `${sub - gap}%`,
      borderRadius: 5, padding: "5px 9px", overflow: "visible", overflowWrap: "anywhere",
      background: s.bg, border: "1px solid rgba(35,60,95,.10)", borderLeft: `4px solid ${s.edge}`, color: s.ink,
      boxShadow: "0 1px 3px rgba(35,60,95,.12)",
      animationDelay: `${delay}ms`,
    }}>
      <div style={{ fontSize: tight ? 9.5 : 10, fontWeight: 700, letterSpacing: ".02em", opacity: dark ? 0.72 : 0.78, lineHeight: 1.05 }}>
        {ev.t}
        {ev.family && <Chip bg={COLORS.famEdge}>+ Families</Chip>}
      </div>
      {(ev.tag || ev.cat === "logistics") && (
        <div style={{
          display: "inline-block", margin: "3px 0",
          background: s.edge, color: "#fff",
          fontSize: tight ? 9.5 : 10.5, fontWeight: 700,
          letterSpacing: ".08em", textTransform: "uppercase",
          padding: "2px 8px", borderRadius: 3, lineHeight: 1.3,
        }}>{ev.tag || s.tag}</div>
      )}
      <div style={{ fontSize: tight ? 11.5 : 13, fontWeight: 700, lineHeight: 1.14 }}>{ev.l}</div>
      {ev.w && <div style={{ fontSize: tight ? 10 : 10.5, fontStyle: "italic", opacity: 0.85, marginTop: 2, lineHeight: 1.2 }}>{ev.w}</div>}
      {ev.n && <div style={{ fontSize: 10.5, opacity: 0.82, marginTop: 2, lineHeight: 1.2 }}>{ev.n}</div>}
      {ev.rota && (
        <ul style={{ margin: "5px 0 0", padding: 0, listStyle: "none", fontSize: 10.5, lineHeight: 1.35, opacity: 0.92 }}>
          {ev.rota.map((r, i) => (
            <li key={i} style={{ display: "flex", gap: 6, borderTop: `1px solid ${dark ? "rgba(255,255,255,.14)" : "rgba(35,60,95,.09)"}`, padding: "2px 0" }}>
              <span style={{ fontWeight: 700, flex: "none", minWidth: r.l ? 74 : 0 }}>{r.t}</span>
              {r.l && <span style={{ opacity: 0.86 }}>{r.l}</span>}
            </li>
          ))}
        </ul>
      )}
      {ev.org && <div style={{ fontSize: 10, fontStyle: "italic", marginTop: 4, color: subInk || "#8a7c52" }}>Organiser: {ev.org}</div>}
    </div>
  );
}

function Milestone({ ms, delay, y }) {
  return (
    <div className="ms" style={{ position: "absolute", top: y(ms.at) + 3, right: 0, display: "flex", alignItems: "center", gap: 6, zIndex: 5, animationDelay: `${delay}ms` }}>
      <span style={{ width: 12, height: 12, borderRadius: "50%", background: "#fff", border: `2.5px solid ${COLORS.gold}`, flex: "none" }} />
      <span style={{ fontSize: 11, fontWeight: 700, color: "#8a7c52", whiteSpace: "nowrap", background: "rgba(255,255,255,.94)", border: `1px dashed ${COLORS.gold}`, borderRadius: 4, padding: "1px 7px" }}>
        {ms.label}
        <span style={{ color: "#9b9384", fontWeight: 700, marginLeft: 6, fontSize: 10.5 }}>{ms.time}</span>
      </span>
    </div>
  );
}

function TimelineDay({ day }) {
  let delay = 0;
  const aStart = day.axisStart ?? DAY_START;
  const aEnd = day.axisEnd ?? DAY_END;
  const ppm = day.ppm || PPM;
  const y = (m) => (m - aStart) * ppm + TOP_PAD;
  const ht = (s, e) => (e - s) * ppm;
  const totalH = (aEnd - aStart) * ppm + TOP_PAD + 30;

  const grid = [];
  for (let m = aStart; m <= aEnd; m += 60) {
    grid.push(<div key={"h" + m} style={{ position: "absolute", left: "var(--gutter)", right: 0, top: y(m), borderTop: `1px solid ${COLORS.grid}` }} />);
    grid.push(<div key={"hl" + m} style={{ position: "absolute", left: 0, width: "var(--gutter)", paddingRight: 11, textAlign: "right", top: y(m), transform: "translateY(-7px)", fontSize: 11, color: "#9b9384", fontWeight: 700 }}>{hour12(m)}</div>);
  }
  for (let m = aStart + 30; m < aEnd; m += 60) {
    grid.push(<div key={"hf" + m} style={{ position: "absolute", left: "var(--gutter)", right: 0, top: y(m), borderTop: "1px solid #f1ecdf" }} />);
  }
  (day.milestones || []).forEach((ms, i) => {
    grid.push(<div key={"r" + i} style={{ position: "absolute", left: "var(--gutter)", right: 0, top: y(ms.at), borderTop: "1px dotted #d6cfbf" }} />);
  });

  const blocks = packEvents(day.events).map((ev, i) => {
    const el = <EventBlock key={"e" + i} ev={ev} delay={delay} y={y} ht={ht} />;
    delay += 30;
    return el;
  });
  const milestoneEls = (day.milestones || []).map((ms, i) => {
    const el = <Milestone key={"m" + i} ms={ms} delay={delay} y={y} />;
    delay += 30;
    return el;
  });

  return (
    <div style={{ overflowX: "auto" }}>
      <div style={{ minWidth: 820 }}>
        <div style={{ position: "relative", height: 34, background: "#fbf8f1", border: `1px solid ${COLORS.grid}`, borderTop: "none", fontSize: 11, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase" }}>
          <div style={{ position: "absolute", left: "var(--gutter)", right: 8, top: 4, bottom: 4, background: COLORS.navy, color: "#fff", borderRadius: 3, display: "flex", alignItems: "center", paddingLeft: 9, overflow: "hidden", whiteSpace: "nowrap" }}>
            {day.head || "All Faculty"}
          </div>
        </div>
        <div className="board" style={{ position: "relative", background: "#fff", border: `1px solid ${COLORS.grid}`, borderTop: "none", borderRadius: "0 0 6px 6px", overflow: "hidden", height: totalH }}>
          {grid}
          <div style={{ position: "absolute", left: "var(--gutter)", right: 8, top: 0, height: totalH }}>
            {blocks}
            {milestoneEls}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Mobile agenda ---------------- */

function AgendaDay({ day }) {
  const items = [];
  day.events.forEach((e) => items.push({ kind: "event", at: e.s, data: e }));
  (day.milestones || []).forEach((m) => items.push({ kind: "ms", at: m.at, data: m }));
  items.sort((a, b) => a.at - b.at || ((a.kind === "ms" ? 0 : 1) - (b.kind === "ms" ? 0 : 1)));

  return (
    <div style={{ background: "#fff", border: `1px solid ${COLORS.grid}`, borderTop: "none", borderRadius: "0 0 6px 6px", padding: "4px 14px 10px" }}>
      {items.map((it, i) => {
        if (it.kind === "ms") {
          const ms = it.data;
          return (
            <div key={"m" + i} style={{ display: "flex", alignItems: "center", gap: 9, padding: "9px 0", borderTop: i ? "1px solid #f0ece2" : "none" }}>
              <span style={{ width: 12, height: 12, borderRadius: "50%", background: "#fff", border: `2.5px solid ${COLORS.gold}`, flex: "none" }} />
              <div style={{ fontSize: 13.5, fontWeight: 700, color: "#8a7c52", border: `1px dashed ${COLORS.gold}`, borderRadius: 4, padding: "2px 8px" }}>
                {ms.label}
                <span style={{ color: "#9b9384", marginLeft: 7, fontSize: 12 }}>{ms.time}</span>
              </div>
            </div>
          );
        }
        const ev = it.data;
        const s = catStyle(ev.cat);
        const dark = ev.cat === "social";
        const subColor = dark ? "rgba(255,255,255,.82)" : "#6b6557";
        return (
          <div key={"e" + i} style={{ display: "flex", gap: 10, padding: dark ? "10px 12px" : "9px 0", margin: dark ? "6px 0" : 0, background: dark ? s.bg : "transparent", borderRadius: dark ? 6 : 0, borderTop: (i && !dark) ? "1px solid #f0ece2" : "none" }}>
            <div style={{ width: 4, borderRadius: 2, background: s.edge, flex: "none" }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: dark ? "rgba(255,255,255,.7)" : "#9b9384", letterSpacing: ".02em" }}>
                {ev.t}
                <span style={{ color: dark ? "#fff" : s.ink, marginLeft: 8, textTransform: "uppercase", letterSpacing: ".06em" }}>{ev.tag || s.tag}</span>
                {ev.family && <span style={{ color: dark ? COLORS.gold : COLORS.famEdge, marginLeft: 8, textTransform: "uppercase", letterSpacing: ".06em" }}>+ Families</span>}
              </div>
              <div style={{ fontSize: 14.5, fontWeight: 700, color: s.ink, lineHeight: 1.18 }}>{ev.l}</div>
              {ev.w && <div style={{ fontSize: 12.5, fontStyle: "italic", color: subColor, marginTop: 1 }}>{ev.w}</div>}
              {ev.n && <div style={{ fontSize: 12.5, color: subColor, marginTop: 1 }}>{ev.n}</div>}
              {ev.rota && (
                <ul style={{ margin: "6px 0 0", padding: 0, listStyle: "none", fontSize: 12.5, lineHeight: 1.4, color: subColor }}>
                  {ev.rota.map((r, k) => (
                    <li key={k} style={{ display: "flex", gap: 8, borderTop: `1px solid ${dark ? "rgba(255,255,255,.14)" : "#f0ece2"}`, padding: "3px 0" }}>
                      <span style={{ fontWeight: 700, flex: "none", color: dark ? "#fff" : s.ink, minWidth: r.l ? 92 : 0 }}>{r.t}</span>
                      {r.l && <span>{r.l}</span>}
                    </li>
                  ))}
                </ul>
              )}
              {ev.org && <div style={{ fontSize: 11.5, fontStyle: "italic", color: dark ? COLORS.gold : "#8a7c52", marginTop: 4 }}>Organiser: {ev.org}</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function NothingScheduled({ nextLine }) {
  return (
    <div style={{ background: "#fff", border: `1px solid ${COLORS.grid}`, borderTop: "none", borderRadius: "0 0 6px 6px", padding: 16 }}>
      <div style={{ border: `1px dashed ${COLORS.gold}`, borderRadius: 6, padding: "18px 20px", background: "repeating-linear-gradient(135deg,#fbf7ee,#fbf7ee 9px,#f6f0e2 9px,#f6f0e2 18px)", color: "#6b6557", fontSize: 13.5, lineHeight: 1.5 }}>
        <div style={{ fontWeight: 700, color: COLORS.navy, marginBottom: 3 }}>Nothing scheduled for you on this day.</div>
        {nextLine}
      </div>
    </div>
  );
}

function DayCard({ day, open, onToggle, isMobile }) {
  const empty = dayIsEmpty(day);
  return (
    <section className="day" style={{ marginTop: 22 }}>
      <div
        className="ofs-day-head" role="button" tabIndex={0} aria-expanded={open}
        onClick={onToggle}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(); } }}
        style={{
          background: COLORS.navy, color: "#fff", borderRadius: open ? "6px 6px 0 0" : 6,
          padding: "13px 18px", display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 14, cursor: "pointer", userSelect: "none", opacity: empty ? 0.72 : 1,
        }}
      >
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 16, flexWrap: "wrap", flex: 1, minWidth: 0 }}>
          <h2 style={{ fontFamily: FONT_DISPLAY, margin: 0, fontSize: 20, fontWeight: 700 }}>{day.meta}</h2>
          <div style={{ color: "#cdd6e2", fontSize: 13, fontWeight: 600, letterSpacing: ".03em" }}>
            <span style={{ color: COLORS.gold, fontWeight: 700 }}>{empty ? "Nothing scheduled for you" : day.title}</span>
          </div>
        </div>
        <span style={{ flex: "none", fontSize: 13, color: COLORS.gold, transform: open ? "rotate(90deg)" : "none", transition: "transform .2s ease", display: "inline-block" }}>▶</span>
      </div>

      {open && (empty
        ? <NothingScheduled nextLine={day.emptyNote || "Check the other days for your schedule."} />
        : (
          <>
            {(day.dress || (day.notes && day.notes.length)) && (
              <div style={{ background: "#fbf8f1", borderLeft: `1px solid ${COLORS.grid}`, borderRight: `1px solid ${COLORS.grid}`, padding: "8px 14px", display: "flex", flexWrap: "wrap", alignItems: "center", gap: "4px 14px", fontSize: 12.5 }}>
                {day.dress && (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: COLORS.navy, fontWeight: 700 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "#fff", background: COLORS.gold, borderRadius: 3, padding: "1px 6px" }}>Dress code</span>
                    {day.dress}
                  </span>
                )}
                {(day.notes || []).map((n, i) => (
                  <span key={i} style={{ color: COLORS.famInk, fontStyle: "italic" }}>{n}</span>
                ))}
              </div>
            )}
            {isMobile ? <AgendaDay day={day} /> : <TimelineDay day={day} />}
          </>
        ))}
    </section>
  );
}

/* ---------------- PDF (respects the audience filter) ---------------- */

const PDF_RGB = {
  navy: [0, 45, 98], gold: [163, 145, 97], red: [238, 49, 36], grid: [232, 227, 216],
  band: [252, 250, 245], inkGray: [107, 101, 87], who: [74, 84, 96],
  marker: [138, 124, 82], white: [255, 255, 255], cdd: [205, 214, 226],
  headFill: [251, 248, 241], markerFill: [250, 246, 236],
};

function loadScript(src) {
  return new Promise((res, rej) => {
    const s = document.createElement("script");
    s.src = src; s.async = true;
    s.onload = res; s.onerror = () => rej(new Error("Failed to load " + src));
    document.head.appendChild(s);
  });
}

async function ensureJsPdf() {
  if (!(window.jspdf && window.jspdf.jsPDF)) {
    await loadScript("https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js");
  }
  if (!(window.jspdf.jsPDF.API && window.jspdf.jsPDF.API.autoTable)) {
    await loadScript("https://cdn.jsdelivr.net/npm/jspdf-autotable@3.8.2/dist/jspdf.plugin.autotable.min.js");
  }
}

function pdfNotes(e) {
  const parts = [];
  if (e.w) parts.push(e.w);
  if (e.n) parts.push(e.n);
  if (e.rota) parts.push(e.rota.map((r) => (r.l ? r.t + ": " + r.l : r.t)).join(" \u00b7 "));
  if (e.org) parts.push("Organiser: " + e.org);
  return parts.join(" \u00b7 ");
}

async function downloadSchedulePdf(days, opts) {
  await ensureJsPdf();
  const { jsPDF } = window.jspdf;
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const PW = doc.internal.pageSize.getWidth();
  const PH = doc.internal.pageSize.getHeight();
  const M = 40;
  const _now = new Date();
  const printedStamp = _now.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) + " \u00b7 " + _now.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

  doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(...PDF_RGB.gold);
  doc.text("KING'S ACADEMY", M, M);
  doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(...PDF_RGB.inkGray);
  doc.text("Printed on " + printedStamp, PW - M, M, { align: "right" });
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...PDF_RGB.navy); doc.setFontSize(20);
  doc.text(opts.docTitle, M, M + 22);
  doc.setFontSize(9); doc.setTextColor(...PDF_RGB.gold);
  doc.text(opts.subtitle, M, M + 36);
  doc.setDrawColor(...PDF_RGB.navy); doc.setLineWidth(1.5);
  doc.line(M, M + 44, PW - M, M + 44);
  doc.setDrawColor(...PDF_RGB.red); doc.setLineWidth(2.5);
  doc.line(M, M + 44, M + 70, M + 44);
  let y = M + 60;

  days.forEach((day) => {
    const items = [];
    (day.events || []).forEach((e) => {
      const base = e.tag || catStyle(e.cat).tag;
      const aud = e.family ? base + " and families" : base;
      items.push({ sort: e.s, time: e.t, label: e.l, aud, who: pdfNotes(e), marker: false });
    });
    (day.milestones || []).forEach((m) => items.push({ sort: m.at, time: m.time, label: m.label, aud: "Transport", who: "", marker: true }));
    items.sort((a, b) => a.sort - b.sort || ((a.marker ? 0 : 1) - (b.marker ? 0 : 1)));

    if (y > PH - M - 80) { doc.addPage(); y = M; }

    const barH = 22;
    doc.setFillColor(...PDF_RGB.navy); doc.rect(M, y, PW - 2 * M, barH, "F");
    doc.setFillColor(...PDF_RGB.red); doc.rect(M, y, 5, barH, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(12); doc.setTextColor(...PDF_RGB.white);
    doc.text(day.meta, M + 8, y + 15);
    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(...PDF_RGB.cdd);
    doc.text(day.title || "", PW - M - 8, y + 15, { align: "right" });
    y += barH;

    if (day.dress) {
      doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(...PDF_RGB.gold);
      doc.text("DRESS CODE: ", M, y + 12);
      const w1 = doc.getTextWidth("DRESS CODE: ");
      doc.setFont("helvetica", "normal"); doc.setTextColor(...PDF_RGB.navy);
      doc.text(day.dress, M + w1, y + 12);
      y += 16;
    }
    if (day.notes && day.notes.length) {
      doc.setFont("helvetica", "italic"); doc.setFontSize(8); doc.setTextColor(...PDF_RGB.inkGray);
      doc.text(day.notes.join("  \u00b7  "), M, y + 11);
      y += 15;
    }
    if (dayIsEmpty(day)) {
      doc.setFillColor(250, 246, 236); doc.rect(M, y, PW - 2 * M, 26, "F");
      doc.setDrawColor(...PDF_RGB.grid); doc.setLineWidth(0.5); doc.rect(M, y, PW - 2 * M, 26, "S");
      doc.setFont("helvetica", "italic"); doc.setFontSize(9); doc.setTextColor(...PDF_RGB.inkGray);
      doc.text("Nothing scheduled for you on this day.", M + 8, y + 17);
      y += 26 + 16;
      return;
    }

    const body = items.map((it) => [it.time, it.label, it.aud, it.who]);
    const markers = items.map((it) => it.marker);

    doc.autoTable({
      startY: y,
      head: [["Time", "Event", "For", "Location / Notes"]],
      body,
      margin: { left: M, right: M },
      styles: { font: "helvetica", fontSize: 9, cellPadding: 4, lineColor: PDF_RGB.grid, lineWidth: 0.5, textColor: [40, 40, 40], valign: "top", overflow: "linebreak" },
      headStyles: { fillColor: PDF_RGB.headFill, textColor: PDF_RGB.navy, fontStyle: "bold", fontSize: 8 },
      alternateRowStyles: { fillColor: PDF_RGB.band },
      columnStyles: {
        0: { cellWidth: 82, textColor: PDF_RGB.inkGray, fontStyle: "bold" },
        1: { cellWidth: 118, fontStyle: "bold", textColor: PDF_RGB.navy },
        2: { cellWidth: 92, textColor: PDF_RGB.inkGray },
        3: { textColor: PDF_RGB.who },
      },
      didParseCell: (d) => {
        if (d.section === "body" && markers[d.row.index]) {
          d.cell.styles.fillColor = PDF_RGB.markerFill;
          if (d.column.index === 1) { d.cell.styles.textColor = PDF_RGB.marker; d.cell.styles.fontStyle = "italic"; }
        }
      },
      didDrawPage: () => {
        const p = doc.internal.getCurrentPageInfo().pageNumber;
        doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(...PDF_RGB.inkGray);
        doc.text(opts.footer, M, PH - 18);
        doc.text(String(p), PW - M, PH - 18, { align: "right" });
      },
    });
    y = doc.lastAutoTable.finalY + 16;
  });

  doc.save(opts.file + ".pdf");
}

/* ---------------- Landing ---------------- */

function Shell({ children }) {
  return (
    <div style={{ fontFamily: FONT_BODY, background: "radial-gradient(1200px 420px at 50% -140px,#fbf9f4,transparent), #E8E6DF", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 20px" }}>
      <style>{`
        .ofs-card { transition: transform .15s ease, box-shadow .15s ease; }
        .ofs-card:hover { transform: translateY(-2px); box-shadow: 0 6px 18px rgba(35,60,95,.13); }
        .ofs-link:hover { text-decoration: underline; }
        .ofs-back-btn:hover { background: #1d2733; color: #fff; }
      `}</style>
      <div style={{ width: "100%", maxWidth: 780, textAlign: "center" }}>{children}</div>
    </div>
  );
}

function Masthead({ sub }) {
  return (
    <>
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 2 }}>
        <Logo height={136} />
      </div>
      <h1 style={{ fontFamily: FONT_DISPLAY, color: COLORS.navy, fontSize: 34, lineHeight: 1.1, margin: "16px 0 6px", fontWeight: 700 }}>Faculty Orientation</h1>
      {sub && <div style={{ color: COLORS.gold, fontWeight: 700, fontSize: 13, letterSpacing: ".06em" }}>{sub}</div>}
      {sub && <div style={{ height: 3, background: COLORS.navy, margin: "20px auto 0", maxWidth: 120 }} />}
    </>
  );
}

const CARD = {
  background: "#fff", border: `1px solid ${COLORS.grid}`, borderRadius: 8,
  padding: "22px 22px", cursor: "pointer", textAlign: "left", fontFamily: FONT_BODY,
  boxShadow: "0 1px 3px rgba(35,60,95,.06)", display: "flex", flexDirection: "column", justifyContent: "space-between",
};

function AudienceChooser({ onPick }) {
  return (
    <Shell>
      <Masthead sub="AUGUST 8–13, 2026 · 2026–2027" />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 14, textAlign: "left", marginTop: 30 }}>
        {AUDIENCES.map((a) => {
          const isAll = a.key === "all";
          return (
            <button key={a.key} type="button" className="ofs-card" onClick={() => onPick(a.key)}
              style={{ ...CARD, minHeight: 64, borderTop: `5px solid ${isAll ? COLORS.gold : COLORS.navy}`, gridColumn: isAll ? "1 / -1" : "auto" }}>
              <div style={{ fontFamily: FONT_DISPLAY, color: COLORS.navy, fontSize: 17, fontWeight: 700 }}>{a.label}</div>
            </button>
          );
        })}
      </div>
    </Shell>
  );
}

/* ---------------- Schedule view ---------------- */

function FacultyTimeline({ aud, onChangeAud }) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(() => DAYS.map(() => false));
  const toggle = (i) => setOpen((p) => p.map((v, idx) => (idx === i ? !v : v)));
  const [pdfBusy, setPdfBusy] = useState(null); // null | "self" | "master"

  const days = DAYS.map((d) => filterDay(d, aud));

  const pdfFor = async (which) => {
    setPdfBusy(which);
    try {
      if (which === "master") {
        await downloadSchedulePdf(DAYS.map((d) => filterDay(d, "all")), {
          docTitle: "Faculty Orientation",
          subtitle: "AUGUST 8\u201313, 2026   \u00b7   MASTER SCHEDULE",
          footer: "King's Academy \u00b7 Faculty Orientation 2026\u20132027 \u00b7 Master Schedule",
          file: "Faculty_Orientation_2026_Master",
        });
      } else {
        await downloadSchedulePdf(days, {
          docTitle: "Faculty Orientation",
          subtitle: "AUGUST 8\u201313, 2026   \u00b7   " + audLabel(aud).toUpperCase(),
          footer: "King's Academy \u00b7 Faculty Orientation 2026\u20132027 \u00b7 " + audLabel(aud),
          file: "Faculty_Orientation_2026_" + (aud === "all" ? "All" : audLabel(aud).replace(/[^A-Za-z]+/g, "_")),
        });
      }
    } catch (e) { window.print(); }
    finally { setPdfBusy(null); }
  };

  return (
    <div className="ofs-root" style={{ "--gutter": "74px", fontFamily: FONT_BODY, color: "#1d2733", lineHeight: 1.3, background: "radial-gradient(1200px 420px at 50% -140px,#fbf9f4,transparent), #E8E6DF", minHeight: "100vh" }}>
      <style>{`
        .evt { opacity: 0; animation: ofsRise .5s ease forwards; }
        .ms { opacity: 0; animation: ofsFade .5s ease forwards; }
        @keyframes ofsRise { from { opacity: 0; transform: translateY(7px); } to { opacity: 1; transform: none; } }
        @keyframes ofsFade { to { opacity: 1; } }
        .ofs-day-head:hover { filter: brightness(1.1); }
        .ofs-pdf-btn:hover { filter: brightness(1.14); }
        .ofs-link:hover { text-decoration: underline; }
        .ofs-back-btn:hover { background: #1d2733; color: #fff; }
        @media (prefers-reduced-motion: reduce) { .evt, .ms { opacity: 1 !important; animation: none !important; } }
        @media (max-width: 600px) { .ofs-wrap { padding: 18px 12px 40px !important; } .ofs-title { font-size: 24px !important; } }
      `}</style>

      <div className="ofs-wrap" style={{ maxWidth: 1140, margin: "0 auto", padding: "26px 26px 60px" }}>
        <button type="button" onClick={onChangeAud} className="ofs-back-btn" style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#fff", border: "1.5px solid #1d2733", borderRadius: 5, padding: "7px 13px", marginBottom: 16, color: "#1d2733", fontFamily: FONT_BODY, fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
          ← All schedules
        </button>

        <div style={{ borderBottom: `3px solid ${COLORS.navy}`, paddingBottom: 16, marginBottom: 8, display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 24, flexWrap: "wrap" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <h1 className="ofs-title" style={{ fontFamily: FONT_DISPLAY, color: COLORS.navy, fontSize: 29, lineHeight: 1.08, margin: "4px 0 0", fontWeight: 700 }}>Faculty Orientation Timeline</h1>
            <div style={{ color: COLORS.gold, fontWeight: 700, fontSize: 13, letterSpacing: ".05em", marginTop: 5 }}>AUGUST 8–13, 2026 &nbsp;·&nbsp; 2026–2027</div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, alignSelf: "flex-start" }}>
            <button type="button" onClick={() => pdfFor("self")} disabled={!!pdfBusy} className="ofs-pdf-btn" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7, background: COLORS.navy, color: "#fff", border: "none", borderRadius: 4, padding: "9px 15px", fontFamily: FONT_BODY, fontSize: 13, fontWeight: 700, cursor: pdfBusy ? "default" : "pointer", opacity: pdfBusy ? 0.7 : 1, whiteSpace: "nowrap" }}>
              <span style={{ fontSize: 15, lineHeight: 1 }}>⤓</span> {pdfBusy === "self" ? "Preparing…" : `PDF — ${audLabel(aud)}`}
            </button>
            <button type="button" onClick={() => pdfFor("master")} disabled={!!pdfBusy} className="ofs-pdf-btn" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7, background: "#fff", color: COLORS.navy, border: `1.5px solid ${COLORS.navy}`, borderRadius: 4, padding: "8px 15px", fontFamily: FONT_BODY, fontSize: 13, fontWeight: 700, cursor: pdfBusy ? "default" : "pointer", opacity: pdfBusy ? 0.7 : 1, whiteSpace: "nowrap" }}>
              <span style={{ fontSize: 15, lineHeight: 1 }}>⤓</span> {pdfBusy === "master" ? "Preparing…" : "Master Schedule (all faculty)"}
            </button>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", background: "#fff", border: `1px solid ${COLORS.grid}`, borderLeft: `5px solid ${COLORS.gold}`, borderRadius: 5, padding: "9px 14px", margin: "16px 0 4px" }}>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "#9b9384" }}>Showing</span>
          <span style={{ fontSize: 14, fontWeight: 700, color: COLORS.navy }}>{audLabel(aud)}</span>
          <button type="button" onClick={onChangeAud} className="ofs-link" style={{ background: "none", border: "none", padding: 0, color: COLORS.gold, fontFamily: FONT_BODY, fontSize: 13, fontWeight: 700, cursor: "pointer", marginLeft: "auto" }}>
            change
          </button>
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 20px", alignItems: "center", margin: "12px 0 6px", fontSize: 12.5, color: "#46505c" }}>
          <LegendItem swatch={<Swatch solid={COLORS.navy} />}>New faculty programme</LegendItem>
          <LegendItem swatch={<Swatch solid={COLORS.gold} />}>All faculty session</LegendItem>
          <LegendItem swatch={<Swatch solid={COLORS.red} />}>Logistics and administration</LegendItem>
          <LegendItem swatch={<Swatch solid="#333B49" />}>Meals and social</LegendItem>
          <LegendItem swatch={<Swatch dot />}>Point-in-time event</LegendItem>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "12px 0 2px", fontSize: 13, fontStyle: "italic", color: "#8a7c52" }}>
          <span style={{ fontSize: 11, color: COLORS.gold, fontStyle: "normal" }}>▶</span>
          Select any day below to expand its schedule.
        </div>

        {days.map((day, i) => (
          <DayCard key={i} day={day} open={open[i]} onToggle={() => toggle(i)} isMobile={isMobile} />
        ))}

        <p style={{ fontSize: 11.5, color: "#6b6557", marginTop: 16, lineHeight: 1.5 }}>
          Tap a day to open or close it.
        </p>
      </div>
    </div>
  );
}

export default function FacultyOrientation() {
  const [aud, setAud] = useState(null);
  if (!aud) return <AudienceChooser onPick={setAud} />;
  return <FacultyTimeline aud={aud} onChangeAud={() => setAud(null)} />;
}

/* ----------------------------------------------------------------------
   ASSUMPTIONS AND SOURCE CONFLICTS — resolve before publishing

   1. Monday 10 August welcome back dinner. The new faculty document says
      18:00-19:30; the v4 all-faculty draft says 18:30-19:30. This file uses
      18:30-19:30 (s: 1110). Change s to 1080 and t to "6:00 - 7:30 pm" if the
      earlier start is correct.
   2. "New Faculty Orientation (August 8-11, 2026)" carries no 11 August
      content. Tuesday 11 August here comes entirely from the v4 all-faculty
      draft. Either the title is wrong or a page is missing.
   3. Saturday 8 August welcome dinner is listed as 18:30-19:00, a 30 minute
      dinner. Left as written.
   4. Sunday 9 August Beit Sitti dinner has a 17:00 start and no end time.
      Blocked out to 19:00 so it renders, and labelled "end time to be
      confirmed".
   5. Wednesday 12 August blood testing and fingerprinting. The source lists
      seven time slots and seven departments in separate cells. The pairing
      here follows document order and is not stated explicitly.
   6. Wednesday and Thursday use different department names for what look like
      the same meetings (History Religion & Society / History, Physical & Life
      Science & Math / STEM, Art Design and Technology / VPA). Left as written.
   7. Thursday 13 August: dinner 18:00-19:30 overlaps the residential life
      meeting at 19:00-20:00. Both render side by side.
   8. Head of School appears as both "Mrs." and "Ms. Penny Townsend" across the
      two sources. "Mrs." used throughout, matching the student orientation app.
   9. Spelling variants left standardised to one form: Kyle Sumrow (not
      Summrow), Refectory Square (not Refractory), Michael Barraclough (also
      appears as Mike Barraclough in the leadership updates list, kept there as
      written).
  10. Gaps with nothing scheduled: Mon 10 Aug 11:15-12:00 and 16:00-18:30,
      Tue 11 Aug 16:00-18:30, Wed 12 Aug 14:45-15:00 and 16:00-18:00.
---------------------------------------------------------------------- */